import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssessmentQueueEntry, AssessmentQueueResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { formatTimestamp } from '../components/profile/attributeDisplay';
import styles from './AssessmentsQueuePage.module.css';

/** Rows shown per person before "Show N more" (Visual QA Round 2). Every
 * entry in this queue is equally due — there is no urgency score to sort
 * by — so this is a length limit only, never a filter: which entries are
 * in the visible slice is decided purely by the API's own order, and the
 * remainder is one click away, not lost. */
const DEFAULT_VISIBLE = 6;

/**
 * Assessments — the queue (`docs/UX_ARCHITECTURE.md` §2's "Assessments" nav
 * item; `docs/DOMAIN_DECISIONS.md` "Assessment Cadence Source and Queue
 * Derivation"; `MVP_SCOPE.md` §2's "minimal — list of pending/recent, not
 * full workflow automation").
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS ALLOWED TO SAY
 *
 * One (employee, attribute) pair per row: due because it has never been
 * assessed, or because its most recent assessment is older than the
 * organisation's cadence policy allows (CQ-1/CQ-3, `GET /assessment-queue`).
 * There is no urgency score, no "days overdue", no ranking by how late a
 * pair is, and no re-sorting of the API's own order — matching the same
 * no-ranking principle `DashboardPage`'s coverage widget already states
 * (`docs/MVP_SCOPE.md` §6). Entries are grouped by employee purely for
 * legibility; the grouping does not reorder anything, because the API
 * already returns one employee's entries together (the caller's
 * visible-employee order, then the attribute library's own order).
 *
 * "Never assessed" and "last assessed on X" are kept as two different
 * sentences, never collapsed into one — an absence of a record and an old
 * record are different facts, exactly as `AttributeRow` already treats them
 * on a profile.
 *
 * This page's own read scope is `resolveVisibleEmployees`, the same
 * resolver behind `/employees`, `/assessment-coverage` and
 * `/assessment-activity` — nobody can appear here who could not already be
 * opened from the directory or the dashboard.
 * ---------------------------------------------------------------------
 */
export function AssessmentsQueuePage() {
  const state = useApiResource<AssessmentQueueResponse>('/assessment-queue');

  if (state.status === 'loading') return <LoadingState label="Loading the assessments queue…" />;

  if (state.status === 'failed') {
    return <ErrorState title="The assessments queue could not be loaded" detail={describeError(state.error)} />;
  }

  const { scope, entries, count, asOf } = state.data;
  const groups = groupByEmployee(entries);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Assessments</h1>
        <p className={styles.scope}>
          {scope === 'organisation'
            ? 'Everyone in your organisation'
            : scope === 'responsibility'
              ? 'The people you are responsible for'
              : 'You and the people you lead'}
          {' · as at '}
          {formatTimestamp(asOf)}
        </p>
      </header>

      <p className={styles.note}>
        Attribute/employee pairs currently due for reassessment under the organisation&rsquo;s assessment cadence
        policy — either never assessed, or last assessed longer ago than the cadence allows. This is not a ranking:
        there is no urgency score here, and people are listed in the same order as everywhere else you can see them.
      </p>

      {count === 0 ? (
        <EmptyState
          title="Nothing is due"
          detail="No attribute is currently due for reassessment for anyone you can see. This does not mean everyone has been fully assessed — see assessment coverage on the dashboard for that."
        />
      ) : (
        <ul className={styles.list} aria-label="Assessments due">
          {groups.map((group) => (
            <QueueGroup key={group.employeeId} group={group} />
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueGroup({ group }: { group: EmployeeGroup }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? group.entries : group.entries.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = group.entries.length - visible.length;

  return (
    <li className={styles.group}>
      <div className={styles.groupHeader}>
        <Link to={`/people/${group.employeeId}`} className={styles.person}>
          {group.fullName}
        </Link>
        <span className={styles.dueCount}>
          {group.entries.length} {group.entries.length === 1 ? 'attribute' : 'attributes'} due
        </span>
      </div>
      <ul className={styles.items}>
        {visible.map((entry) => (
          <li key={entry.attribute.id} className={styles.item}>
            <span className={styles.attribute}>{entry.attribute.name}</span>
            <span className={styles.when}>
              {entry.mostRecentAssessedAt ? (
                `Last assessed ${formatTimestamp(entry.mostRecentAssessedAt)}`
              ) : (
                <span className={styles.neverAssessed}>Never assessed</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {group.entries.length > DEFAULT_VISIBLE ? (
        <button
          type="button"
          className={styles.moreToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
        </button>
      ) : null}
    </li>
  );
}

interface EmployeeGroup {
  employeeId: string;
  fullName: string;
  entries: AssessmentQueueEntry[];
}

/** Groups consecutive entries by employee, preserving the API's own order —
 * never sorting or re-grouping non-adjacent entries, since the API already
 * guarantees one employee's pairs arrive together. */
function groupByEmployee(entries: readonly AssessmentQueueEntry[]): EmployeeGroup[] {
  const groups: EmployeeGroup[] = [];
  for (const entry of entries) {
    const current = groups[groups.length - 1];
    if (current && current.employeeId === entry.employeeId) {
      current.entries.push(entry);
    } else {
      groups.push({ employeeId: entry.employeeId, fullName: entry.fullName, entries: [entry] });
    }
  }
  return groups;
}
